import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../apiClient';
import { createD2DSale } from '../../api/salesApi';
import Button from '../../components/ui/button';
import { Table, THead, TRow, TCell } from '../../components/ui/table';
import RevivalHeader from '../../components/Revival/RevivalHeader';
import { formatCurrency } from '../../utils/formatters';
import '../../components/Revival/revival.css';
import './sales-ds.css';

const text = (v) => String(v || '').trim();

const FREQUENCIES = ['one_time', 'weekly', 'biweekly', 'monthly', 'quarterly', 'annually'];
const FREQ_LABELS = {
  one_time: 'One-Time',
  weekly: 'Weekly',
  biweekly: 'Bi-Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annually: 'Annually',
};

const EMPTY_SERVICE = { type: '', frequency: 'monthly', price: '' };

const searchCustomers = async (query) => {
  if (!query || query.length < 2) return [];
  try {
    const r = await api.get('/customers/', { params: { search: query, page_size: 6 } });
    const d = r?.data;
    return Array.isArray(d?.results) ? d.results : Array.isArray(d) ? d : [];
  } catch {
    return [];
  }
};

export default function D2DSaleForm() {
  const navigate = useNavigate();

  // ── Customer ──
  const [customerName, setCustomerName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');

  // ── Customer search ──
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);

  // ── Services ──
  const [services, setServices] = useState([{ ...EMPTY_SERVICE }]);

  // ── Scheduling ──
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('09:00');

  // ── Options ──
  const [notes, setNotes] = useState('');
  const [pricingLocked, setPricingLocked] = useState(false);
  const [sendQuote, setSendQuote] = useState(false);
  const [autoConvert, setAutoConvert] = useState(false);

  // ── State ──
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // ── Customer search handler ──
  const handleNameSearch = useCallback(async (query) => {
    setCustomerName(query);
    if (query.length >= 2) {
      const results = await searchCustomers(query);
      setSearchResults(results);
      setSearchOpen(results.length > 0);
    } else {
      setSearchResults([]);
      setSearchOpen(false);
    }
  }, []);

  const selectCustomer = (c) => {
    const name =
      text(c.company_name) ||
      text(c.full_name) ||
      [text(c.first_name), text(c.last_name)].filter(Boolean).join(' ') ||
      '';
    setCustomerName(name);
    setPhone(text(c.primary_phone || c.phone || c.phone_number));
    setEmail(text(c.primary_email || c.email));
    setAddress(text(c.address || c.service_address));
    setCity(text(c.city));
    setState(text(c.state));
    setZipCode(text(c.zip_code || c.zip));
    setSearchOpen(false);
    setSearchResults([]);
  };

  // ── Services handlers ──
  const updateService = (i, field, value) => {
    setServices((prev) => prev.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)));
  };
  const addService = () => setServices((prev) => [...prev, { ...EMPTY_SERVICE }]);
  const removeService = (i) => {
    setServices((prev) => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== i)));
  };

  const totalPrice = services.reduce((sum, s) => sum + (Number(s.price) || 0), 0);

  // ── Submit ──
  const handleSubmit = async () => {
    if (!text(customerName)) {
      toast.error('Customer name is required');
      return;
    }
    if (!text(address)) {
      toast.error('Service address is required');
      return;
    }
    const validServices = services.filter((s) => text(s.type) && Number(s.price) > 0);
    if (validServices.length === 0) {
      toast.error('Add at least one service with type and price');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        customer_name: text(customerName),
        address: text(address),
        phone: text(phone) || undefined,
        email: text(email) || undefined,
        city: text(city) || undefined,
        state: text(state) || undefined,
        zip_code: text(zipCode) || undefined,
        services: validServices.map((s) => ({
          type: text(s.type),
          frequency: s.frequency || 'monthly',
          price: Number(s.price) || 0,
        })),
        notes: text(notes) || undefined,
        source: 'd2d',
        pricing_source: 'manual',
        pricing_locked: pricingLocked,
        send_quote: sendQuote,
        auto_convert: pricingLocked && autoConvert,
      };
      if (scheduleEnabled && scheduleDate) {
        payload.schedule_date = scheduleDate;
        payload.schedule_time = scheduleTime || '09:00';
      }

      const response = await createD2DSale(payload);
      setResult(response?.data || response);
      toast.success('Sale created successfully');
    } catch (err) {
      const detail =
        text(err?.response?.data?.detail) ||
        text(err?.response?.data?.error) ||
        text(err?.message) ||
        'Failed to create sale';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setResult(null); setCustomerName(''); setPhone(''); setEmail(''); setAddress(''); setCity(''); setState(''); setZipCode('');
    setServices([{ ...EMPTY_SERVICE }]); setNotes(''); setScheduleEnabled(false); setScheduleDate('');
    setPricingLocked(false); setSendQuote(false); setAutoConvert(false);
  };

  // ── Success view ──
  if (result) {
    return (
      <div className="rv-screen">
        <RevivalHeader
          title="Sale created"
          totals={[result.customer_created ? 'New customer' : 'Matched an existing customer', result.scheduled ? 'First service scheduled' : null]}
          actions={(
            <>
              <Button variant="secondary" size="sm" onClick={resetForm}>New sale</Button>
              {result.appointment_id ? <Button variant="secondary" size="sm" onClick={() => navigate('/schedule/day')}>Open dispatch</Button> : null}
            </>
          )}
          primary={result.quote_id ? <Button variant="primary" size="sm" onClick={() => navigate(`/quotes/${result.quote_id}`)}>View quote</Button> : null}
        />
        <dl className="sl-kv">
          <div><dt>Customer</dt><dd>#{result.customer_id}</dd></div>
          {result.appointment_id ? <div><dt>Appointment</dt><dd>{String(result.appointment_id).slice(0, 8)}</dd></div> : null}
          {result.quote_id ? <div><dt>Quote</dt><dd>{result.quote_number || `#${result.quote_id}`}</dd></div> : null}
          {result.quote_created ? (
            <div><dt>Quote status</dt><dd>{result.quote_sent ? 'Sent' : 'Draft'}{result.auto_converted ? ' · auto-converted' : ''}</dd></div>
          ) : null}
          {result.first_service ? <div><dt>First service</dt><dd>Yes</dd></div> : null}
          {result.appointment_reused ? <div><dt>Note</dt><dd>Existing appointment reused</dd></div> : null}
        </dl>
        {result.pricing_warnings && result.pricing_warnings.length > 0 ? (
          <div className="sl-notice is-warn">
            <b>Pricing warnings</b>
            <ul>{result.pricing_warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
          </div>
        ) : null}
      </div>
    );
  }

  // ── Form ──
  return (
    <div className="rv-screen">
      <RevivalHeader
        title={text(customerName) || 'New sale'}
        totals={[<>Total <b>{formatCurrency(totalPrice)}</b></>, scheduleEnabled ? 'Scheduling first service' : null, sendQuote ? 'Quote will be sent' : null]}
        actions={<Button variant="ghost" size="sm" onClick={() => navigate(-1)}>Back</Button>}
        primary={(
          <Button variant="primary" size="sm" onClick={handleSubmit} loading={submitting} disabled={submitting}>
            {submitting ? 'Creating…' : 'Create sale'}
          </Button>
        )}
      />

      <div className="sl-form">
        <section className="sl-pane">
          <h2 className="sl-h">Customer</h2>
          <label className="sl-field" style={{ position: 'relative' }}>
            Name
            <input value={customerName} onChange={(e) => handleNameSearch(e.target.value)} placeholder="Search existing or type a new name" />
            {searchOpen && (
              <div className="ds-menu__list sl-suggest" role="listbox">
                {searchResults.map((c) => {
                  const label =
                    text(c.company_name) || text(c.full_name) ||
                    [text(c.first_name), text(c.last_name)].filter(Boolean).join(' ') || 'Customer';
                  return (
                    <button key={c.id || c.customer_id} type="button" role="option" aria-selected="false" className="ds-menu__item" onClick={() => selectCustomer(c)}>
                      <b>{label}</b> <span className="sl-muted">{text(c.primary_phone || c.phone) || text(c.primary_email || c.email)}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </label>
          <label className="sl-field">Phone<input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" /></label>
          <label className="sl-field">Email<input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="customer@example.com" type="email" /></label>
        </section>

        <section className="sl-pane">
          <h2 className="sl-h">Service address</h2>
          <label className="sl-field">Street<input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="123 Main St" /></label>
          <div className="sl-form" style={{ '--sl-cols': 3 }}>
            <label className="sl-field">City<input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Tampa" /></label>
            <label className="sl-field">State<input value={state} onChange={(e) => setState(e.target.value)} placeholder="FL" maxLength={2} /></label>
            <label className="sl-field">ZIP<input value={zipCode} onChange={(e) => setZipCode(e.target.value)} placeholder="33601" maxLength={10} /></label>
          </div>
        </section>
      </div>

      <section className="sl-section">
        <div className="sl-section-head">
          <h2>Services</h2>
          <p>Price is per visit.</p>
          <div className="sl-end"><Button variant="secondary" size="sm" onClick={addService}>Add service</Button></div>
        </div>
        <Table>
          <THead columns={['Service', 'Frequency', { label: 'Price', num: true }, '']} />
          <tbody>
            {services.map((s, i) => (
              <TRow key={i}>
                <TCell><input className="sl-input" aria-label="Service" value={s.type} onChange={(e) => updateService(i, 'type', e.target.value)} placeholder="Lawn care, pest treatment…" /></TCell>
                <TCell>
                  <select className="sl-input" aria-label="Frequency" value={s.frequency} onChange={(e) => updateService(i, 'frequency', e.target.value)}>
                    {FREQUENCIES.map((f) => <option key={f} value={f}>{FREQ_LABELS[f]}</option>)}
                  </select>
                </TCell>
                <TCell num>
                  <input className="sl-input sl-num" aria-label="Price" type="number" min="0" step="0.01" value={s.price} onChange={(e) => updateService(i, 'price', e.target.value)} placeholder="0.00" />
                </TCell>
                <TCell>
                  {services.length > 1 ? <Button variant="ghost" size="sm" onClick={() => removeService(i)} aria-label="Remove service">Remove</Button> : null}
                </TCell>
              </TRow>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2} className="is-num sl-strong">Total</td>
              <td className="is-num sl-strong">{formatCurrency(totalPrice)}</td>
              <td />
            </tr>
          </tfoot>
        </Table>
      </section>

      <div className="sl-form">
        <section className="sl-pane">
          <h2 className="sl-h">First service</h2>
          <label className="sl-check">
            <input type="checkbox" checked={scheduleEnabled} onChange={(e) => setScheduleEnabled(e.target.checked)} />
            Schedule the first service
          </label>
          {scheduleEnabled ? (
            <div className="sl-form">
              <label className="sl-field">Date<input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} /></label>
              <label className="sl-field">Time<input type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} /></label>
            </div>
          ) : null}
        </section>

        <section className="sl-pane">
          <h2 className="sl-h">Quote and pricing</h2>
          <label className="sl-check">
            <input type="checkbox" checked={pricingLocked} onChange={(e) => { setPricingLocked(e.target.checked); if (!e.target.checked) setAutoConvert(false); }} />
            Lock pricing (no negotiation)
          </label>
          <label className="sl-check">
            <input type="checkbox" checked={sendQuote} onChange={(e) => setSendQuote(e.target.checked)} />
            Send the quote to the customer
          </label>
          <label className={`sl-check${!pricingLocked ? ' is-off' : ''}`}>
            <input type="checkbox" checked={autoConvert} disabled={!pricingLocked} onChange={(e) => setAutoConvert(e.target.checked)} />
            Auto-convert to an agreement{!pricingLocked ? ' (needs locked pricing)' : ''}
          </label>
        </section>
      </div>

      <section className="sl-section">
        <label className="sl-field">
          Notes for the tech on the first service
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Gate code 1234, dog in back yard, treat north side first…" />
        </label>
      </section>
    </div>
  );
}
